/**
 * @file Anything that can run on every page.
 */

import debounce from 'just-debounce-it';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

/**
 * Global functions.
 *
 * Runs component js and any global tasks.
 */
const vGlobal = () => {
	if (wpPostData.isFrontPage) {
		gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

		// create the scrollSmoother before your scrollTriggers
		ScrollSmoother.create({
			smooth: 1, // how long (in seconds) it takes to "catch up" to the native scroll position
			effects: true, // looks for data-speed and data-lag attributes on elements
			// smoothTouch: 0.1, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
		});
	}

	import('../components/header/header').then(({ default: vHeader }) => {
		vHeader();
	});
	import('../components/mobile-nav/mobile-nav').then(
		({ default: vMobileNav }) => {
			vMobileNav();
		},
	);
	import('../components/search-bar/search-bar').then(
		({ default: vSearchBar }) => {
			vSearchBar();
		},
	);

	if (document.querySelector('.v-hp-splash')) {
		import('../components/hp-splash/hp-splash').then(
			({ default: vHpSplash }) => {
				vHpSplash();
			},
		);
	}

	if (document.querySelector('.v-hp-tagline')) {
		import('../components/hp-tagline/hp-tagline').then(
			({ default: vHpTagline }) => {
				vHpTagline();
			},
		);
	}

	if (document.querySelector('.v-hp-hero')) {
		import('../components/hp-hero/hp-hero').then(({ default: vHpHero }) => {
			vHpHero();
		});
	}

	if (document.querySelector('.v-markets')) {
		import('../components/markets/markets').then(({ default: vMarkets }) => {
			vMarkets();
		});
	}

	// if (document.querySelector('.v-hp-equipment')) {
	// 	import('../components/hp-equipment/hp-equipment').then(
	// 		({ default: vHpEquipment }) => {
	// 			vHpEquipment();
	// 		},
	// 	);
	// }

	// if (document.querySelector('.v-hp-smallwares')) {
	// 	import('../components/hp-smallwares/hp-smallwares').then(
	// 		({ default: vHpSmallwares }) => {
	// 			vHpSmallwares();
	// 		},
	// 	);
	// }

	if (document.querySelector('.v-hp-test-kitchen')) {
		import('../components/hp-test-kitchen/hp-test-kitchen').then(
			({ default: vHpTestKitchen }) => {
				vHpTestKitchen();
			},
		);
	}

	if (document.querySelector('.v-hp-showroom')) {
		import('../components/hp-showroom/hp-showroom').then(
			({ default: vHpShowroom }) => {
				vHpShowroom();
			},
		);
	}

	if (document.querySelector('.v-gallery-with-content')) {
		import('../components/gallery-with-content/gallery-with-content').then(
			({ default: vGalleryWithContent }) => {
				vGalleryWithContent();
			},
		);
	}

	if (document.querySelector('.v-form-overlay')) {
		import('../components/form-overlay/form-overlay').then(
			({ default: vFormOverlay }) => {
				vFormOverlay();
			},
		);
	}

	if (document.querySelector('.v-media-list')) {
		import('../components/media-list/media-list').then(
			({ default: vMediaList }) => {
				vMediaList();
			},
		);
	}

	import('../components/footer/footer').then(({ default: vFooter }) => {
		vFooter();
	});

	/**
	 * Get scrollbar width.
	 * @returns {number} Scrollbar width.
	 */
	function getScrollbarWidth() {
		const outer = document.createElement('div');
		outer.style.visibility = 'hidden';
		outer.style.overflow = 'scroll';
		document.body.appendChild(outer);

		const inner = document.createElement('div');
		outer.appendChild(inner);

		const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

		outer.parentNode.removeChild(outer);

		return scrollbarWidth;
	}

	document.documentElement.style.setProperty(
		'--scrollbar-width',
		`${getScrollbarWidth()}px`,
	);
	window.addEventListener(
		'resize',
		debounce(() => {
			document.documentElement.style.setProperty(
				'--scrollbar-width',
				`${getScrollbarWidth()}px`,
			);
		}),
		100,
	);

	const selects = document.querySelectorAll('select');
	[...selects].forEach((select) => {
		const selectParent = select.parentNode;
		if (selectParent.classList.contains('v-form__select')) {
			return;
		}

		const wrapper = document.createElement('span');
		const arrow = document.createElement('span');
		wrapper.classList.add('v-form__select');
		arrow.classList.add('v-form__select-arrow');
		selectParent.insertBefore(wrapper, select);
		wrapper.appendChild(select);
		wrapper.appendChild(arrow);
		select.classList.add('v-form__select-field');
	});

	const tableWrappers = document.querySelectorAll('.v-table-wrapper');
	/**
	 * Toggle scroll indicator classes on table wrappers.
	 * @param {Element} tableWrapper - Table wrapper.
	 * @param {Element} tableInner - Scrollable inner table wrapper.
	 */
	const tableToggleClasses = (tableWrapper, tableInner) => {
		if (tableInner.scrollLeft > 2) {
			tableWrapper.classList.add('v-table-wrapper--scrollable-start');
		} else {
			tableWrapper.classList.remove('v-table-wrapper--scrollable-start');
		}

		if (
			tableInner.scrollLeft + tableInner.clientWidth <
			tableInner.scrollWidth - 2
		) {
			tableWrapper.classList.add('v-table-wrapper--scrollable-end');
		} else {
			tableWrapper.classList.remove('v-table-wrapper--scrollable-end');
		}
	};

	tableWrappers.forEach((tableWrapper) => {
		const inner = tableWrapper.querySelector('.v-table-wrapper__inner');
		inner.addEventListener(
			'scroll',
			tableToggleClasses.bind(null, tableWrapper, inner),
			{ passive: true },
		);

		const observer = new ResizeObserver(() => {
			tableToggleClasses(tableWrapper, inner);
		});
		observer.observe(tableWrapper, { box: 'border-box' });
		tableToggleClasses(tableWrapper, inner);
	});

	// Floating labels
	const floatingLabels = document.querySelectorAll('.v-form__floating-label');
	if (floatingLabels.length) {
		const floatingInputs = [...floatingLabels].map((l) => {
			const wrap = l.parentElement;
			return wrap.querySelector('input, select, textarea');
		});

		[...floatingInputs].forEach((field, i) => {
			field.addEventListener('focusin', () => {
				floatingLabels[i].classList.add('v-form__floating-label--active');
			});

			field.addEventListener('focusout', () => {
				if (!field.value) {
					floatingLabels[i].classList.remove('v-form__floating-label--active');
				}
			});

			field.addEventListener('change', () => {
				if (!field.value) {
					floatingLabels[i].classList.remove('v-form__floating-label--active');
				}
			});

			const fieldObserver = new MutationObserver((e) => {
				if (!field.value) {
					floatingLabels[i].classList.remove('v-form__floating-label--active');
				}
			});
			fieldObserver.observe(field, { attributes: true });
		});
	}

	/**
	 * Global Window Load.
	 *
	 * Runs polyfills on page load.
	 */
	async function onWindowLoad() {
		const body = document.querySelector('body');
		if (!body.classList.contains('admin-bar')) {
			const { listen } = await import('quicklink');

			listen({
				ignores: [
					(url) => {
						const cleanUrl = url.replace(/#.*/, '');
						return cleanUrl === window.location.href;
					},
					/.*\/wp-admin\/.*/,
				],
			});
		}
	}

	if (document.readyState === 'complete') {
		onWindowLoad();
	} else {
		window.addEventListener('load', onWindowLoad);
	}
};

if (document.readyState !== 'loading') {
	vGlobal();
} else {
	document.addEventListener('DOMContentLoaded', vGlobal);
}
